import React, { useState, useEffect, useRef, useMemo } from 'react';
import Loader from '@lwt-helix/loader';
import { CustomInput } from '@lwt-helix/controls';
import AgentTransactionCoverageChart from './transaction-coverage/AgentTransactionCoverageChart';
import TransactionCoverageTable from './transaction-coverage/TransactionCoverageTable';
import TableChartToggle, { tableChartModes } from '../../common/TableChartToggle';
import { coverageChartTooltips, dealStatusCodes } from '../../../constants/agentProductionConstants';
import { agentProductionTerms, areaRequestTypes } from '../../../../constants';
import { useQuery } from '../../../../common/hooks/location';
import { getElementTooltip } from '../../../helpers/uiHelpers';
import { getAgentTransactionCoverage } from '../../../../service/service-gateway';
import PropTypes from 'prop-types';
import { usePDFStore } from '../../../../store/pdf/store';
import { setStateData } from '../../../../common/helpers/state';
import AreaTypeDropdown from '../../common/HelixDropdown';
import StatusDropdown from '../../common/HelixDropdown';
import ChartDataType from '../../common/HelixDropdown'
import { chartDataTypeOptions, statusTypeOptions } from '../../helpers/agentProfile';

const TransactionCoverageTab = props => {
    const {
        searchCriteria,
        mlsId,
        agentId,
        tableChartMode,
        timePeriod,
        setIsTimePeriodSelectorDisabled,
        sendToParent,
        isActive,
        agent,
        mappedAreas,
        transactionCoverageMode,
        mlsName,
        setState,
        setShouldStickyFooterHide
    } = props;
    const gridRef = useRef();

    const query = useQuery();
    const queryAreaType = query.get('areaType');
    const graphStatusType = query.get('graphStatusType');
    const queryIsUnits = query.get('isUnits'); //tracks the volume or units value
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [areasMap, setAreasMap] = useState([]);
    const [areaType, setAreaType] = useState(null);
    const [statusType, setStatusType] = useState(dealStatusCodes.SOLD);
    const [isUnits, setIsUnits] = useState(queryIsUnits === 'true');
    const [shouldDisplayAreaPercentage, setShouldDisplayAreaPercentage] = useState(true);

    const {
        isPDFLoading,
        setAgentProfileArea,
        areasData,
        setAreasData,
    } = usePDFStore(state => ({
        isPDFLoading: state.downloadModalStatus.isPDFLoading,
        setAgentProfileArea: state.setAgentProfileArea,
        areasData: state.areasData,
        setAreasData: state.setAreasData,
    }));

    const dataRef = useRef(data);
    useEffect(() => {
        dataRef.current = data;
    });
    useEffect(() => {
        switch (graphStatusType) {
            case dealStatusCodes.SOLD.value:
                setStatusType(dealStatusCodes.SOLD);
                break;
            case dealStatusCodes.ACTIVE.value:
                setStatusType(dealStatusCodes.ACTIVE);
                break;
            case dealStatusCodes.UNDER_CONTRACT.value:
                setStatusType(dealStatusCodes.UNDER_CONTRACT);
                break;
        }
    }, [graphStatusType]);

    useEffect(() => {
        let areasMap, areaType;
        areasMap = mappedAreas && !!Object.values(mappedAreas).length ? Object.values(mappedAreas) : [];
        const searchFields = searchCriteria?.realEstateDatasourceIdsWithFilters[0]
            .searchFields ?? [];
        const isSearchAllMLS = searchCriteria?.realEstateDatasourceIdsWithFilters[0]
            .searchAllMLS;
        const searchFieldNames = searchFields.map(f => f.fieldName) || [];
        // Set areaType to the matching searchField columnName
        searchFieldNames.forEach(fieldName => {
            areasMap.forEach(area => {
                if (areaRequestTypes[area.columnName].toLowerCase() === fieldName.toLowerCase()) {
                    areaType = area.columnName;
                }
            });
        });

        // Set areaType to areaMap columnName with the viewName "MLS Areas"
        // if searching "All MLS" and areaType is still null
        if (areaType == null && isSearchAllMLS) {
            areasMap.forEach(area => {
                if (area.viewName.toLowerCase() === agentProductionTerms.mlsAreas.toLowerCase()) {
                    areaType = area.columnName;
                }
            });
        }

        // Default areaType to first option if areaType is still null
        if (areaType == null) {
            areaType = areasMap[0]?.columnName;
        }

        setAreasMap(areasMap);
        setAreaType(queryAreaType || areaType);
    }, [mappedAreas]);

    useEffect(() => {
        if (!mlsId || !agentId || !areaType || !searchCriteria) return;

        const loadTransactionCoverageData = async () => {
            setIsLoading(true);
            try {
                const areaRequestType = areaRequestTypes[areaType];
                const response = await getAgentTransactionCoverage(
                    searchCriteria, agentId, areaRequestType, statusType.value, timePeriod
                );
                if (!response.error) {
                    setData(response);
                }
            } catch (error) {
                console.error('Failed to load transaction coverage data: ', error);

            } finally {
                setIsLoading(false);
            }
        };

        loadTransactionCoverageData();
    }, [agentId, areaType, statusType, timePeriod, isActive, searchCriteria]);

    useEffect(() => {
        if (!isActive || isLoading) return;
        setShouldStickyFooterHide(false);
        const area = areasMap.find(a => a.columnName === areaType)?.viewName;
        const dataForParent = {
            data: dataRef.current,
            tableChartMode: tableChartMode,
            area,
            statusType: statusType,
            gridRef,
            shouldDisplayAreaPercentage
        };
        setAgentProfileArea(area);
        sendToParent(dataForParent);

    }, [isActive, tableChartMode, areaType, statusType, dataRef.current, isLoading]);

    const chartButtons =
        tableChartMode === tableChartModes.CHART ? (
            <ChartDataType
                tooltips={coverageChartTooltips}
                options={chartDataTypeOptions}
                currentValue={
                    isUnits ? coverageChartTooltips.unitsBtn.id : coverageChartTooltips.volumeBtn.id
                }
                onChange={(id) => setIsUnits(id === coverageChartTooltips.unitsBtn.id)}
            />
        ) : null;

    const sortedData = useMemo(() => {
        const sorted = data?.slice();
        const sortBy = isUnits ? 'totalUnits' : 'totalVolume';
        sorted?.sort((a, b) => a[sortBy] < b[sortBy] ? 1 : -1);
        setAreasData(sorted);
        return sorted;
    }, [data, isUnits]);    
    const createChart = ({ isPDF }) => (
        <AgentTransactionCoverageChart
            agentData={isPDF ? areasData.filter(area => area.checked === true) : sortedData}
            isUnits={isUnits}
            statusType={statusType.value}
            areaType={areaType}
            tableType={statusType}
            agent={agent}
            timePeriod={timePeriod}
            shouldDisplayAreaPercentage={shouldDisplayAreaPercentage}
            isPDF={isPDF}
        />
    );

    const chartContent = createChart({ isPDF: false });

    const pdfChartContent = isPDFLoading ? createChart({ isPDF: true }) : null;

    const tableContent = useMemo(() => {
        const label = areaType
            ? areasMap.find(a => a.columnName === areaType)?.viewName
            : '';

        return (<TransactionCoverageTable
            gridRef={gridRef}
            areaLabel={label}
            data={data}
            isUnits={isUnits}
            statusType={statusType.value}
            areaType={areaType}
            tableType={statusType}
            agent={agent}
            timePeriod={timePeriod}
        />);
    }, [data, statusType, isUnits]);

    let displayedContent;
    if (isLoading) {
        displayedContent = <Loader />;
    } else if (tableChartMode === tableChartModes.TABLE) {
        displayedContent = tableContent;
    } else {
        displayedContent = chartContent;
    }

    const areaTypeOptions = [];
    areasMap?.forEach(a => {
        areaTypeOptions.push({value: a.columnName, label: a.viewName});
    });

    const statusOptions = [
        { value:  dealStatusCodes.ACTIVE.value, label:  dealStatusCodes.ACTIVE.label},
        { value:  dealStatusCodes.UNDER_CONTRACT.value, label:  dealStatusCodes.UNDER_CONTRACT.label},
        { value:  dealStatusCodes.SOLD.value, label:  dealStatusCodes.SOLD.label}
    ];

    const handleStatusButtonClick = (statusType, isDisabled) => {
        const selectedStatusTypeOption = statusOptions.find(s => s.value === statusType);
        setStatusType(statusTypeOptions[selectedStatusTypeOption?.value]);
        setIsTimePeriodSelectorDisabled(isDisabled);
    };

    return (
        <>
            <div className='controls-container mb-3'>
                <TableChartToggle
                    defaultMode={transactionCoverageMode}
                    parentSetMode={(m) => setStateData('transactionCoverageMode', m, setState)}
                    hasAreaSelector={true}
                    mlsName={mlsName}
                />
                <div className='d-flex justify-content-end align-items-center flex-wrap' style={{ gap: '8px' }}>
                    {tableChartMode === tableChartModes.CHART && (
                        <div className='d-flex mr-2 align-items-center'>
                            <CustomInput
                                dataLwtId='areaPercentageCheckbox'
                                type='checkbox'
                                checked={shouldDisplayAreaPercentage}
                                id='exampleCustomCheckbox'
                                label={getElementTooltip(coverageChartTooltips.marketAreaCheck)}
                                onChange={() =>
                                    setShouldDisplayAreaPercentage(!shouldDisplayAreaPercentage)
                                }
                            />
                        </div>
                    )}
                    <div className='agent-coverage-controls-item ml-3'>
                        <AreaTypeDropdown
                            currentValue={areaType}
                            options={areaTypeOptions}
                            onChange={setAreaType}
                        />
                    </div>

                    <div className='mx-3'>
                        <StatusDropdown
                            tooltips={coverageChartTooltips}
                            options={statusOptions}
                            currentValue={statusType.value}                            
                            onChange={handleStatusButtonClick}/>                      
                    </div>
                    {chartButtons}
                </div>
            </div>
            {displayedContent}
            {pdfChartContent}
            <div className='hidden-export-table'>{/* This class hides the table used as a reference to export the table without being displayed  */}
                {tableContent}
            </div>
        </>
    );
};

TransactionCoverageTab.propTypes = {
    searchCriteria: PropTypes.object,
    mlsId: PropTypes.number,
    agentId: PropTypes.string,
    tableChartMode: PropTypes.string.isRequired,
    timePeriod: PropTypes.string.isRequired,
    setIsTimePeriodSelectorDisabled: PropTypes.func.isRequired,
    sendToParent: PropTypes.func.isRequired,
    isActive: PropTypes.bool.isRequired,
    agent: PropTypes.object,
    mappedAreas: PropTypes.object,
    transactionCoverageMode: PropTypes.string,
    setState: PropTypes.func,
    mlsName: PropTypes.string,
    setShouldStickyFooterHide: PropTypes.func.isRequired,
};

export default TransactionCoverageTab;
